import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

/**
 * The frame every administrative screen sits in.
 *
 * These pages had each grown their own heading, spacing and filter row, using raw h1 tags and <br/>
 * for layout, so no two lined up with each other. Giving them one shell means a change to how an
 * admin page is presented happens once rather than ten times, and a new screen inherits the layout
 * instead of reinventing it.
 *
 * The content sits on a card only on larger screens: on a phone, a card inside the page margin just
 * spends horizontal space on borders that the content needs.
 */
export default function PageShell(props) {
    const { title, subtitle, actions, toolbar, children, isMobile, disableCard } = props;

    const framed = !isMobile && !disableCard;

    return (
        <Box sx={{ paddingBottom: '40px' }}>
            <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent={'space-between'}
                alignItems={isMobile ? 'stretch' : 'flex-end'}
                spacing={1}
                sx={{ marginBottom: '16px' }}
            >
                <Box>
                    <Typography
                        variant={'h4'}
                        component={'h1'}
                        sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
                    >
                        {title}
                    </Typography>
                    {subtitle &&
                        <Typography variant={'body2'} sx={{ color: 'text.secondary', marginTop: '4px' }}>
                            {subtitle}
                        </Typography>
                    }
                </Box>

                {actions &&
                    <Stack direction={'row'} spacing={1} sx={{ flexShrink: 0 }}>
                        {actions}
                    </Stack>
                }
            </Stack>

            <Paper
                elevation={0}
                sx={framed ? {
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                } : {
                    backgroundColor: 'transparent',
                }}
            >
                {toolbar &&
                    <>
                        <Box sx={{ padding: framed ? '16px' : '0 0 16px' }}>
                            <Stack
                                direction={'row'}
                                spacing={1.5}
                                useFlexGap
                                sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                            >
                                {toolbar}
                            </Stack>
                        </Box>
                        {framed && <Divider />}
                    </>
                }

                <Box sx={{ padding: framed && !toolbar ? '0' : undefined }}>
                    {children}
                </Box>
            </Paper>
        </Box>
    );
}

PageShell.propTypes = {
    /** Shown as the page heading. */
    title: PropTypes.string.isRequired,

    /** Optional line under the title, typically a count of what is on screen. */
    subtitle: PropTypes.node,

    /** Buttons aligned to the right of the heading. */
    actions: PropTypes.node,

    /** Search boxes, filters and date pickers, laid out in a row that wraps. */
    toolbar: PropTypes.node,

    children: PropTypes.node,
    isMobile: PropTypes.bool,

    /** Renders without the card, for a page that draws its own panels. */
    disableCard: PropTypes.bool,
};
